/**
 * Provider Manager with fallback support
 * Automatically tries multiple providers if one fails
 */

import { AIProvider, ProviderConfig, ProviderError } from './types';
import { OpenAIText } from '../providers/text/openai';
import { AlkahestText } from '../providers/text/alkahest';
import { MahoText } from '../providers/text/maho';
import { AkashText } from '../providers/text/akash';

export interface ProviderFallbackConfig {
  primary: string;
  fallbacks: string[];
  configs: Record<string, ProviderConfig>;
}

export class ProviderManager {
  private providers = new Map<string, AIProvider>();
  private fallbackOrder: string[] = [];

  constructor(private config: ProviderFallbackConfig) {
    this.fallbackOrder = [config.primary, ...config.fallbacks];
  }

  async initialize(): Promise<void> {
    // Initialize all configured providers
    for (const providerName of this.fallbackOrder) {
      const providerConfig = this.config.configs[providerName];
      if (!providerConfig) continue;

      let provider: AIProvider;

      switch (providerName) {
        case 'maho':
          provider = new MahoText();
          break;
        case 'alkahest':
          provider = new AlkahestText();
          break;
        case 'akash':
          provider = new AkashText();
          break;
        case 'openai':
          provider = new OpenAIText();
          break;
        default:
          console.warn(`Unknown provider: ${providerName}`);
          continue;
      }

      try {
        await provider.initialize(providerConfig);
        this.providers.set(providerName, provider);
        console.log(`✅ Initialized ${providerName} provider`);
      } catch (error) {
        console.warn(`⚠️ Failed to initialize ${providerName}:`, error);
      }
    }
  }

  async generate(prompt: string, options?: any): Promise<any> {
    let lastError: Error | null = null;

    for (const providerName of this.fallbackOrder) {
      const provider = this.providers.get(providerName);
      if (!provider) continue;

      try {
        console.log(`🔄 Trying ${providerName} provider...`);
        const result = await (provider as any).generate(prompt, options);
        console.log(`✅ Success with ${providerName}`);
        return result;
      } catch (error: any) {
        console.warn(`❌ ${providerName} failed:`, error.message);
        lastError = error;
        
        // If rate limited, wait before trying next provider
        if (error instanceof ProviderError && error.details?.retryAfter) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    throw new ProviderError(
      `All providers failed. Last error: ${lastError?.message}`,
      'provider-manager',
      lastError
    );
  }

  async generateWithMessages(messages: any[], options?: any): Promise<any> {
    let lastError: Error | null = null;

    for (const providerName of this.fallbackOrder) {
      const provider = this.providers.get(providerName);
      if (!provider) continue;

      try {
        console.log(`🔄 Trying ${providerName} provider...`);
        const result = await (provider as any).generateWithMessages(messages, options);
        console.log(`✅ Success with ${providerName}`);
        return result;
      } catch (error: any) {
        console.warn(`❌ ${providerName} failed:`, error.message);
        lastError = error;
        
        // If rate limited, wait before trying next provider
        if (error instanceof ProviderError && error.details?.retryAfter) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    throw new ProviderError(
      `All providers failed. Last error: ${lastError?.message}`,
      'provider-manager',
      lastError
    );
  }

  getAvailableProviders(): string[] {
    return Array.from(this.providers.keys());
  }

  getProvider(name: string): AIProvider | undefined {
    return this.providers.get(name);
  }
}